import { useState } from 'react';
import { X } from 'lucide-react';
import { useRenameFolder } from '../../hooks/queries/useDossierFiles';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import type { Folder } from '../../services/files/filesService';

interface Props {
  dossierId: string;
  folder: Folder;
  onClose: () => void;
}

export function RenameFolderModal({ dossierId, folder, onClose }: Props) {
  const [name, setName] = useState(folder.name);
  const rename = useRenameFolder(dossierId, folder.parent_id);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || name.trim() === folder.name) {
      onClose();
      return;
    }
    try {
      await rename.mutateAsync({ folderId: folder.id, name });
      onClose();
    } catch {
      /* affiché via rename.isError */
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-md shadow-xl w-full max-w-sm"
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
          <h2 className="font-heading text-lg font-semibold">Renommer le dossier</h2>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-5 py-4 space-y-3">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nom du dossier"
            maxLength={100}
          />
          {rename.isError && (
            <p className="text-xs text-red-600">
              {rename.error instanceof Error ? rename.error.message : 'Erreur'}
            </p>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <Button variant="ghost" type="button" onClick={onClose} disabled={rename.isPending}>
            Annuler
          </Button>
          <Button type="submit" disabled={!name.trim() || rename.isPending}>
            {rename.isPending ? 'Enregistrement…' : 'Renommer'}
          </Button>
        </footer>
      </form>
    </div>
  );
}
